import { useApiQuery } from '../useApi';
import { apiClient } from '../../api/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';
import userManager from '../../auth/authConfig';

export interface ProfileBrand {
  id: string;
  name: string;
  favoriteTest?: string;
  hasProfilation?: boolean;
}

export interface Profile {
  userId: string;
  cognitoId: string;
  email: string;
  name: string;
  surname: string;
  phone?: string;
  avatarUrl?: string;
  privacy?: string;
  termsAndCond?: string;
  newsletter?: string;
  thirdParty?: string;
  profilation?: string;
  bcId?: string;
  brands: ProfileBrand[];
}

export type UpdateProfileInput = {
  name?: string;
  surname?: string;
  phone?: string;
  newsletter?: string;
  thirdParty?: string;
  termsAndCond?: string;
  avatarUrl?: string;
  favoriteTest?: string;
  brandId?: string;
};

export const useGetProfile = () => {
  const {
    data: profile,
    isFetching,
    isSuccess,
    error,
  } = useApiQuery<Profile>(
    '/profile',
    ['profile'],
    {},
    { staleTime: 60 * 60 * 1000, gcTime: 60 * 60 * 1000 },
  );

  return { profile, isFetching, isSuccess, error };
};

export const useUpdateProfile = (
  options?: Omit<
    UseMutationOptions<Profile, Error, UpdateProfileInput>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<Profile, Error, UpdateProfileInput>({
    mutationFn: (data) => apiClient.put<Profile>('/profile', data),
    ...options,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await userManager.signinSilent();
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
